import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Alert,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import moment from 'moment'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import BigModal from '../../../components/Modals/BigModal'
import AddNewPGD from './AddNewPGD'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { useNavigate } from 'react-router-dom'
import { UserContext } from '../../../App'

const PGDLibrary = () => {
  const [pgds, setPGDs] = useState([])
  const [openAddNew, setOpenAddNew] = useState(false)
  const [view, setView] = useState('current')
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const userTeams = user.employee?.teams ?? []
  const canManage = userTeams.includes('Governance')

  // Staff only ever get in-date PGDs. Governance also get the ones past their
  // review date and the archive, so a lapsed PGD can be replaced or retired
  // instead of silently dropping out of the library.
  const getPGDs = () => {
    API.get(
      canManage
        ? '/governance/pgdlist?includeExpired=true&includeArchived=true'
        : '/governance/pgdlist'
    ).then((res) => {
      if (res.status === 200) {
        setPGDs(res.data)
      }
    })
  }

  useEffect(() => getPGDs(), [])

  const inView = (status) =>
    pgds.filter((pgd) => (pgd.status ?? 'current') === status)
  const pastReviewCount = inView('pastReview').length

  return (
    <Stack gap={1}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
      >
        <Typography variant="title2">PGD Library</Typography>
        {canManage && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAddNew(true)}
          >
            Add New
          </Button>
        )}
      </Stack>
      {canManage && (
        <>
          {pastReviewCount > 0 && (
            <Alert severity="warning">
              {pastReviewCount === 1
                ? '1 PGD is past its review date.'
                : `${pastReviewCount} PGDs are past their review date.`}{' '}
              Staff can no longer see or sign{' '}
              {pastReviewCount === 1 ? 'it' : 'them'}, and{' '}
              {pastReviewCount === 1 ? 'it counts' : 'they count'} as out of
              date in your KPIs. Open each one to upload the reviewed version,
              or archive it if it has already been replaced or withdrawn.
            </Alert>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(e, value) => value && setView(value)}
          >
            <ToggleButton value="current" sx={{ textTransform: 'none' }}>
              Current ({inView('current').length})
            </ToggleButton>
            <ToggleButton value="pastReview" sx={{ textTransform: 'none' }}>
              Past review date ({pastReviewCount})
            </ToggleButton>
            <ToggleButton value="archived" sx={{ textTransform: 'none' }}>
              Archived ({inView('archived').length})
            </ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
      <CustomDataGrid
        norowsoverlay={
          view === 'current' ? (
            <Alert severity="info">
              No PGDs uploaded yet. Governance team members can use Add New to
              upload each Patient Group Direction — clinicians then read and
              sign them here, with versions tracked.
            </Alert>
          ) : (
            <Alert severity="info">
              {view === 'pastReview'
                ? 'No PGDs are past their review date.'
                : 'No archived PGDs.'}
            </Alert>
          )
        }
        rows={canManage ? inView(view) : pgds}
        getRowId={(row) => row._id}
        columns={[
          { field: 'title', headerName: 'Title', minWidth: 200, flex: 1 },
          {
            field: 'version',
            headerName: 'Version',
            minWidth: 200,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'reviewDate',
            headerName: 'Review date',
            minWidth: 160,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (value) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
          },
          {
            field: 'isSigned',
            headerName: 'Signed',
            headerAlign: 'center',
            align: 'center',
            minWidth: 120,
            renderCell: ({ value }) =>
              value ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="error" />
              ),
          },
        ]}
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnResize
        slots={{
          footer: () => null,
          toolbar: () => null,
        }}
        onRowClick={({ row }) => navigate('/pgd-library/' + row._id)}
      />
      <BigModal open={openAddNew} onClose={() => setOpenAddNew(false)}>
        <AddNewPGD
          onCloseSubmit={() => {
            setOpenAddNew(false)
            getPGDs()
          }}
        />
      </BigModal>
    </Stack>
  )
}

export default PGDLibrary
